import {
  VegaButtonCircle,
  VegaDatePicker,
  VegaFlex,
  VegaFont,
  VegaGrid,
  VegaInput,
  VegaInputSelect,
} from '@globalpayments/vega-react';
import {
  KirbyFilterClauseNode,
  KirbyFilterPropertyDefinition,
  KirbyFilterValidationIssue,
} from '../../../types/kirbyFiltering';
import styles from '../index.module.css';

interface FilterClauseRowProps {
  clause: KirbyFilterClauseNode;
  definitions: KirbyFilterPropertyDefinition[];
  index: number;
  issues: KirbyFilterValidationIssue[];
  onChange: (update: (clause: KirbyFilterClauseNode) => KirbyFilterClauseNode) => void;
  onRemove: () => void;
  removeDisabled: boolean;
}

const issueFor = (issues: KirbyFilterValidationIssue[], field: KirbyFilterValidationIssue['field']) =>
  issues.find((issue) => issue.field === field)?.message;

/** Renders one filter clause from normalized Kirby property metadata. */
const FilterClauseRow = ({
  clause,
  definitions,
  index,
  issues,
  onChange,
  onRemove,
  removeDisabled,
}: FilterClauseRowProps) => {
  const definition = definitions.find((candidate) => candidate.property.value === clause.property);
  const isDateInput = definition?.propertyInputType === 'Date';
  const isNumericInput = definition?.propertyInputType === 'Number';
  const isSupportedInput = definition && ['Date', 'Number', 'Text', 'Other'].includes(definition.propertyInputType);
  const isNullOperator = clause.operator === 'IsNull' || clause.operator === 'IsNotNull';
  const propertyIssue = issueFor(issues, 'property');
  const operatorIssue = issueFor(issues, 'operator');
  const valueIssue = issueFor(issues, 'value');

  const handlePropertyChange = (property: string) => {
    const defaultOperator =
      definitions.find((candidate) => candidate.property.value === property)?.availableOperators[0]?.value ?? '';
    onChange((currentClause) => ({ ...currentClause, property, operator: defaultOperator, value: '' }));
  };

  return (
    <VegaFlex className={styles.clauseRow} direction="col" gap="size-12">
      <VegaFlex alignItems="start" justifyContent="space-between">
        <VegaFont as="h3" color="text-secondary" variant="font-h6">
          Clause {index + 1}
        </VegaFont>
        <VegaButtonCircle
          disabled={removeDisabled}
          icon="fa-solid fa-trash"
          label="Remove clause"
          onVegaClick={onRemove}
          showTooltip
          size="small"
          variant="icon-only"
        />
      </VegaFlex>
      <VegaGrid column={{ default: 1, L: 3 }} gap="size-16">
        <VegaInputSelect
          data-testid={`${clause.id}-property`}
          hint={propertyIssue}
          isValid={propertyIssue ? false : undefined}
          label="Property"
          onVegaChange={(event) => {
            if (typeof event.detail === 'string') handlePropertyChange(event.detail);
          }}
          placeholder="Select a property"
          required
          selectType="single"
          source={definitions.map(({ property }) => ({ id: property.value, displayName: property.label }))}
          value={clause.property}
        />
        <VegaInputSelect
          data-testid={`${clause.id}-operator`}
          disabled={!definition}
          hint={operatorIssue}
          isValid={operatorIssue ? false : undefined}
          label="Operator"
          onVegaChange={(event) => {
            const operator = event.detail;
            if (typeof operator === 'string') {
              onChange((currentClause) => ({ ...currentClause, operator, value: '' }));
            }
          }}
          placeholder="Select an operator"
          required
          selectType="single"
          source={(definition?.availableOperators ?? []).map((operator) => ({
            id: operator.value,
            displayName: operator.label,
          }))}
          value={clause.operator}
        />
        {isDateInput ? (
          <VegaDatePicker
            data-testid={`${clause.id}-value`}
            disabled={isNullOperator}
            format="YYYY-MM-DD"
            isValid={valueIssue ? false : undefined}
            label="Value"
            mode="single"
            onVegaChange={(event) => {
              const value = event.detail;
              if (typeof value === 'string') onChange((currentClause) => ({ ...currentClause, value }));
            }}
            placeholder="Select a date"
            required
            showClearIcon
            value={clause.value}
          />
        ) : (
          <VegaInput
            data-testid={`${clause.id}-value`}
            disabled={!definition || !isSupportedInput || isNullOperator}
            hint={
              valueIssue ??
              (definition && !isSupportedInput
                ? `${definition.propertyInputType} values are not available in this first pass.`
                : isNullOperator
                  ? 'Null-check submission is awaiting Kirby contract confirmation.'
                  : undefined)
            }
            isValid={valueIssue ? false : undefined}
            label="Value"
            onVegaChange={(event) => onChange((currentClause) => ({ ...currentClause, value: event.detail }))}
            placeholder="Enter a value"
            required
            showClearIcon
            type={isNumericInput ? 'number' : 'text'}
            value={clause.value}
          />
        )}
      </VegaGrid>
    </VegaFlex>
  );
};

export default FilterClauseRow;
